import React from 'react';
import type { CartItem } from '@/Context/CartContext';
import { formatPrice } from '@/Services/format';
import { cartLabels } from '@/data/content';

interface GroupFood {
    name: string;
    portion_label?: string;
}

interface SummaryRow {
    type: 'menu' | 'individual';
    menuName?: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    groups?: {
        groupName: string;
        foods: GroupFood[];
    }[];
    itemName?: string;
    portion_label?: string;
}

function buildSummaryRows(items: { item: CartItem; index: number }[]): SummaryRow[] {
    const rows: SummaryRow[] = [];
    const menuGroups = new Map<string, { item: CartItem; index: number }[]>();
    const individualItems: { item: CartItem; index: number }[] = [];

    for (const entry of items) {
        if (entry.item.menu_instance_id) {
            const key = entry.item.menu_instance_id;
            const existing = menuGroups.get(key) ?? [];
            existing.push(entry);
            menuGroups.set(key, existing);
        } else {
            individualItems.push(entry);
        }
    }

    for (const [, menuItems] of menuGroups) {
        const menuName = menuItems[0]?.item.menu_name ?? 'Menu';
        const menuQuantity = Math.max(1, ...menuItems.map((e) => e.item.quantity));

        const groupedByGroup = new Map<string, GroupFood[]>();
        let menuTotal = 0;

        for (const { item } of menuItems) {
            const gName = item.menu_group_name || '';
            const existing = groupedByGroup.get(gName) ?? [];
            existing.push({
                name: item.name,
                portion_label: item.portion_label,
            });
            groupedByGroup.set(gName, existing);
            menuTotal += item.price * item.quantity;
        }

        const unitPrice = menuQuantity > 0 ? menuTotal / menuQuantity : 0;

        const groups = Array.from(groupedByGroup.entries()).map(([groupName, foods]) => ({
            groupName,
            foods,
        }));

        rows.push({
            type: 'menu',
            menuName,
            quantity: menuQuantity,
            unitPrice,
            totalPrice: menuTotal,
            groups,
        });
    }

    for (const { item } of individualItems) {
        const qty = item.quantity;
        const total = item.price * qty;
        rows.push({
            type: 'individual',
            itemName: item.name,
            portion_label: item.portion_label,
            quantity: qty,
            unitPrice: item.price,
            totalPrice: total,
        });
    }

    return rows;
}

interface Props {
    items: { item: CartItem; index: number }[];
}

const CheckoutOrderSummary: React.FC<Props> = ({ items }) => {
    const rows = buildSummaryRows(items);

    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th scope="col" className="py-2 text-left font-semibold text-app-muted">{cartLabels.article_label}</th>
                    <th scope="col" className="py-2 text-center font-semibold text-app-muted">{cartLabels.qty_label}</th>
                    <th scope="col" className="py-2 text-right font-semibold text-app-muted">{cartLabels.unit_price_label}</th>
                    <th scope="col" className="py-2 text-right font-semibold text-app-muted">{cartLabels.total}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => {
                    const rowKey = row.menuName ?? row.itemName ?? i;
                    return (
                        <React.Fragment key={rowKey}>
                            {row.type === 'menu' ? (
                                <>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                        <td className="py-2.5 font-bold text-app-text">{row.menuName}</td>
                                        <td className="py-2.5 text-center text-app-text">{row.quantity}</td>
                                        <td className="py-2.5 text-right text-app-text">
                                            {formatPrice(row.unitPrice)}
                                        </td>
                                        <td className="py-2.5 text-right font-semibold text-app-text">
                                            {formatPrice(row.totalPrice)}
                                        </td>
                                    </tr>
                                    {row.groups?.map((group) => (
                                        <React.Fragment key={group.groupName || 'standalone'}>
                                            {group.groupName && (
                                                <tr>
                                                    <td colSpan={4} className="py-1 pl-4 text-xs italic text-app-muted">
                                                        {group.groupName}
                                                    </td>
                                                </tr>
                                            )}
                                            {group.foods.map((food, fi) => (
                                                <tr key={food.name + fi}>
                                                    <td colSpan={4} className="py-0.5 pl-8 text-sm text-app-text">
                                                        · {food.name}
                                                        {food.portion_label && (
                                                            <span className="text-app-muted">
                                                                {' '}
                                                                ({food.portion_label})
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                    <tr>
                                        <td colSpan={4} className="py-1" />
                                    </tr>
                                </>
                            ) : (
                                <tr className="border-b border-gray-100 dark:border-gray-800">
                                    <td className="py-2.5 text-app-text">
                                        {row.itemName}
                                        {row.portion_label && (
                                            <span className="text-app-muted"> ({row.portion_label})</span>
                                        )}
                                    </td>
                                    <td className="py-2.5 text-center text-app-text">{row.quantity}</td>
                                    <td className="py-2.5 text-right text-app-text">{formatPrice(row.unitPrice)}</td>
                                    <td className="py-2.5 text-right font-semibold text-app-text">
                                        {formatPrice(row.totalPrice)}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
};

export default CheckoutOrderSummary;
